/**
 * DueSoonReminder, a gentle "homework due soon" heads-up.
 *
 * Reusable across the home dashboard and the My Homework page. Fetches the
 * learner's saved homework and shows a compact banner for anything not yet
 * done that's due TODAY or TOMORROW. Renders nothing when there's nothing due,
 * so it's safe to drop anywhere.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarClock } from "lucide-react";
import { API, useApp } from "../context";
import { getActiveLearnerId } from "../hooks/useActiveLearner";

const SUBJECT_LABEL = {
  math: "Math", spelling: "Spelling", reading: "Reading", science: "Science",
  spanish: "Spanish", writing: "Writing", general: "Homework",
};

export default function DueSoonReminder({ className = "" }) {
  const navigate = useNavigate();
  const { user } = useApp();
  const userId = getActiveLearnerId(user?.id || user?.email) || user?.id || user?.email;
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    axios.get(`${API}/tutor/homework/list/${encodeURIComponent(userId)}?include_done=true`)
      .then((r) => { if (alive) setSessions(r.data.sessions || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [userId]);

  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const dueSoon = useMemo(
    () => sessions.filter((s) => !s.completed && s.due_date && (s.due_date === today || s.due_date === tomorrow)),
    [sessions, today, tomorrow]
  );

  if (!userId || dueSoon.length === 0) return null;

  return (
    <div className={`rounded-2xl border border-amber-400/35 bg-gradient-to-r from-amber-500/15 to-orange-500/10 p-3 flex items-start gap-3 ${className}`} data-testid="dashboard-due-reminder">
      <div className="w-9 h-9 rounded-xl bg-amber-500/25 border border-amber-300/40 flex items-center justify-center flex-shrink-0">
        <CalendarClock size={18} className="text-amber-100" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-amber-50 font-fredoka font-bold text-sm">
          Heads up! {dueSoon.length} {dueSoon.length === 1 ? "assignment is" : "assignments are"} due soon 🔔
        </p>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {dueSoon.slice(0, 4).map((s) => (
            <button
              key={s.id}
              onClick={() => navigate("/learn/homework")}
              data-testid={`dashboard-due-item-${s.id}`}
              className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-amber-500/20 border border-amber-300/40 text-amber-50 hover:bg-amber-500/30 transition-colors"
            >
              {s.due_date === today ? "Today" : "Tomorrow"}: {s.title || SUBJECT_LABEL[s.subject] || "Homework"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
